import { createApp } from "vue";
import { Capacitor } from "@capacitor/core";
import App from "./App.vue";
import branding from "./branding";
import i18n from "./i18n";
import store from "./store";
import router from "./router";
import "./assets/css/nucleo-icons.css";
import "./assets/css/nucleo-svg.css";
import VueSweetalert2 from "vue-sweetalert2";
import MaterialDashboard from "./material-dashboard";
import ElementPlus from 'element-plus';
import 'element-plus/dist/index.css';
import 'element-plus/theme-chalk/dark/css-vars.css';
import * as ElementPlusIconsVue from '@element-plus/icons-vue';
import en from 'element-plus/es/locale/lang/en';
import "./registerServiceWorker";
import ToastPlugin from "./plugins/toast";

const appInstance = createApp(App);

for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
    appInstance.component(key, component);
  }

appInstance.use(ElementPlus, {locale: en});
appInstance.use(i18n);
appInstance.use(store);
appInstance.use(router);
appInstance.use(VueSweetalert2);
appInstance.use(MaterialDashboard);
appInstance.use(ToastPlugin)

// Apply brand-specific body class and favicon
if (branding.bodyClass) {
  document.body.classList.add(branding.bodyClass);
}
if (branding.favicon) {
  // Update ALL icon link tags (PWA plugin injects favicon-16x16 and favicon-32x32 variants
  // that browsers prefer over the generic one — all must point to the correct brand icon)
  document.querySelectorAll("link[rel~='icon']").forEach(link => {
    link.href = branding.favicon;
  });
  // Ensure at least one icon link exists
  if (!document.querySelector("link[rel~='icon']")) {
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = branding.favicon;
    document.head.appendChild(link);
  }
}
if (branding.themeColor) {
  let meta = document.querySelector("meta[name='theme-color']");
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.content = branding.themeColor;
}
document.title = branding.name;

// Native iOS app: no page zoom (iOS otherwise zooms in on focused inputs under 16px), and
// run edge-to-edge so the navbar extends under the status bar (safe-area CSS: .native-app).
// The website keeps pinch-zoom (maximum-scale=1.5 in index.html) for accessibility.
if (Capacitor.isNativePlatform()) {
  document.querySelector("meta[name='viewport']")
    ?.setAttribute('content', 'width=device-width,initial-scale=1.0,maximum-scale=1.0,user-scalable=no,viewport-fit=cover');
  document.body.classList.add('native-app');
} else if (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
  // iOS Safari / PWA: maximum-scale=1 stops the focus zoom, while iOS still allows pinch-zoom.
  // Not applied elsewhere — Android Chrome (and the TWA) would block pinch-zoom entirely.
  document.querySelector("meta[name='viewport']")
    ?.setAttribute('content', 'width=device-width,initial-scale=1.0,maximum-scale=1.0');
}

// Switch PWA manifest and Apple home-screen meta for Golfspor
if (branding.name === 'Golfspor') {
  // Point the manifest at the Golfspor-specific manifest file
  let manifestLink = document.querySelector("link[rel='manifest']");
  if (!manifestLink) {
    manifestLink = document.createElement('link');
    manifestLink.rel = 'manifest';
    document.head.appendChild(manifestLink);
  }
  manifestLink.href = '/manifest-golfspor.json';

  // Update Apple PWA title shown on the home screen
  const appleTitle = document.querySelector("meta[name='apple-mobile-web-app-title']");
  if (appleTitle) appleTitle.content = 'Golfspor';

  // Update all Apple touch icons to use the Golfspor icon
  document.querySelectorAll("link[rel~='apple-touch-icon']").forEach(link => {
    link.href = '/img/icons/golfspor-1024x1024.png';
  });
}

appInstance.mount("#app");

// Native iOS app: hide the launch splash once the first route has rendered.
if (Capacitor.isNativePlatform()) {
  const hideSplash = () => import('@capacitor/splash-screen')
    .then(({ SplashScreen }) => SplashScreen.hide({ fadeOutDuration: 200 }));
  router.isReady().then(() => requestAnimationFrame(hideSplash));
  setTimeout(hideSplash, 5000); // never leave the app stuck behind the splash
}

// Native iOS app: tapping a push notification opens the page it links to (Web Push does the
// same in public/sw-push.js). Registered right away so a tap that launched the app is handled.
if (Capacitor.isNativePlatform()) {
  import('@capacitor/push-notifications').then(({ PushNotifications }) => {
    PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
      router.push(notification.data?.url || '/');
    });
  });
}
